<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Button from '../../../../components-next/button/Button.vue';
import BulkStageFilter from './BulkStageFilter.vue';

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
});

// Fase 4.1: emit confirm payload now is `{ cardIds, pipelineStage }`
// (was just `cardIds`). Parent handler must accept the object form.
const emit = defineEmits(['close', 'confirm']);
const { t } = useI18n();
const store = useStore();

const selectedItems = ref([]);
const avatarErrors = ref({});

// Fase 4.3 — stage filter (default 'all' = no filter sent to backend)
const stageFilter = ref('all');

// Pipeline stages for the BulkStageFilter dropdown
const stages = computed(() => {
  const pipeline = store.getters['pipelines/getSelectedPipeline'];
  if (!pipeline?.stages) return [];
  return Object.entries(pipeline.stages)
    .map(([id, stage]) => ({ id, name: stage.name, position: stage.position }))
    .sort((a, b) => a.position - b.position);
});

// Items the user actually sees in the list — narrowed by stage filter.
// All other helpers (toggleSelectAll, isAllSelected) operate on this view.
const filteredItems = computed(() => {
  if (stageFilter.value === 'all') return props.items;
  return props.items.filter(
    item => (item.pipeline_stage || item.stage) === stageFilter.value
  );
});

const getAvatarUrl = item => {
  const details = item.item_details;
  if (details?.contact_thumbnail?.trim()) return details.contact_thumbnail;
  const conv = details?.conversation?.contact;
  if (conv?.thumbnail?.trim()) return conv.thumbnail;
  if (conv?.avatar_url?.trim()) return conv.avatar_url;
  if (item.contact?.thumbnail?.trim()) return item.contact.thumbnail;
  if (item.contact?.avatar_url?.trim()) return item.contact.avatar_url;
  return null;
};

const getContactName = item => {
  const conv = item.item_details?.conversation?.contact;
  if (conv?.name) return conv.name;
  if (item.contact?.name) return item.contact.name;
  return item.item_details?.title || item.title || '?';
};

const getInitials = item => {
  const name = getContactName(item);
  return name.charAt(0).toUpperCase();
};

const handleAvatarError = itemId => {
  avatarErrors.value = { ...avatarErrors.value, [itemId]: true };
};

const priorityConfig = priority => {
  const map = {
    urgent: { bg: 'bg-n-ruby-3', text: 'text-n-ruby-11' },
    high: { bg: 'bg-orange-3', text: 'text-orange-11' },
    medium: { bg: 'bg-n-amber-3', text: 'text-n-amber-11' },
    low: { bg: 'bg-blue-3', text: 'text-blue-11' },
  };
  return map[priority] || { bg: 'bg-n-alpha-2', text: 'text-n-slate-10' };
};

const toggleSelectAll = () => {
  const visibleIds = filteredItems.value.map(item => item.id);
  const allVisibleSelected = visibleIds.every(id =>
    selectedItems.value.includes(id)
  );

  if (allVisibleSelected) {
    // Deselect only the visible ones, keep selection from other stages
    selectedItems.value = selectedItems.value.filter(
      id => !visibleIds.includes(id)
    );
  } else {
    // Add all visible to existing selection (dedup)
    selectedItems.value = Array.from(
      new Set([...selectedItems.value, ...visibleIds])
    );
  }
};

const isAllSelected = computed(() => {
  const visibleIds = filteredItems.value.map(item => item.id);
  return (
    visibleIds.length > 0 &&
    visibleIds.every(id => selectedItems.value.includes(id))
  );
});

const toggleItem = itemId => {
  const index = selectedItems.value.indexOf(itemId);
  if (index === -1) {
    selectedItems.value.push(itemId);
  } else {
    selectedItems.value.splice(index, 1);
  }
};

const handleConfirm = () => {
  if (props.isSubmitting || selectedItems.value.length === 0) return;

  // Fase 4.1 — emit payload object so parent can route through bulk endpoint
  // with optional pipeline_stage filter. Backward-compat: parent is updated
  // to accept this shape (see PipelineHeader#handleBulkDelete).
  emit('confirm', {
    cardIds: selectedItems.value,
    pipelineStage: stageFilter.value === 'all' ? null : stageFilter.value,
  });
};
</script>

<template>
  <div class="w-full max-w-[600px]">
    <div class="p-4 sm:p-6">
      <h3 class="text-heading-2 text-n-slate-12 mb-1 break-words">
        {{ t('PIPELINE.BULK_ACTIONS.DELETE.TITLE') }}
      </h3>
      <p class="text-body-main text-n-slate-11 mb-4">
        {{ t('PIPELINE.BULK_ACTIONS.DELETE.DESCRIPTION') }}
      </p>

      <!-- Fase 4.3 — stage filter: "All" (default) or "Only stage X" -->
      <div v-if="stages.length > 1" class="mb-4">
        <BulkStageFilter
          v-model="stageFilter"
          :items="items"
          :stages="stages"
        />
      </div>

      <!-- Items List -->
      <div
        class="max-h-96 overflow-y-auto mb-4 border border-n-weak rounded-xl"
      >
        <!-- Select All -->
        <label
          class="flex min-h-11 items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-n-weak hover:bg-n-alpha-1 transition-colors rounded-t-xl"
        >
          <input
            type="checkbox"
            :checked="isAllSelected"
            :disabled="isSubmitting"
            class="size-4 flex-shrink-0 accent-n-brand"
            @change="toggleSelectAll"
          />
          <span class="text-label font-medium text-n-slate-12">
            {{ t('PIPELINE.BULK_ACTIONS.SELECT_ALL') }}
          </span>
          <span class="ml-auto text-label-small text-n-slate-10">
            {{
              selectedItems.filter(id => filteredItems.some(i => i.id === id))
                .length
            }}/{{ filteredItems.length }}
          </span>
        </label>

        <!-- Individual Items (narrowed by stageFilter) -->
        <label
          v-for="(item, index) in filteredItems"
          :key="item.id"
          class="flex min-h-11 items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors"
          :class="[
            selectedItems.includes(item.id)
              ? 'bg-n-ruby-2 dark:bg-n-ruby-3/20'
              : 'hover:bg-n-alpha-1',
            index < filteredItems.length - 1 ? 'border-b border-n-weak' : '',
            index === filteredItems.length - 1 ? 'rounded-b-xl' : '',
          ]"
        >
          <input
            type="checkbox"
            :checked="selectedItems.includes(item.id)"
            :disabled="isSubmitting"
            class="size-4 flex-shrink-0 accent-n-brand"
            @change="toggleItem(item.id)"
          />

          <!-- Avatar -->
          <div class="flex-shrink-0">
            <img
              v-if="getAvatarUrl(item) && !avatarErrors[item.id]"
              :src="getAvatarUrl(item)"
              :alt="getContactName(item)"
              class="size-8 rounded-full object-cover ring-2 ring-n-solid-2"
              @error="handleAvatarError(item.id)"
            />
            <div
              v-else
              class="size-8 rounded-full bg-n-alpha-3 ring-2 ring-n-solid-2 flex items-center justify-center"
            >
              <span class="text-xs font-bold text-n-brand">
                {{ getInitials(item) }}
              </span>
            </div>
          </div>

          <!-- Info -->
          <div class="flex-1 min-w-0">
            <p class="text-label font-medium text-n-slate-12 truncate">
              {{
                item.item_details?.title ||
                item.title ||
                t('PIPELINE.ITEM_DETAILS.UNTITLED_ITEM')
              }}
            </p>
            <div class="flex items-center gap-1.5 mt-0.5">
              <span
                class="px-1.5 py-0.5 text-label-small font-medium rounded-full"
                :class="[
                  priorityConfig(
                    item.item_details?.priority || item.priority || 'none'
                  ).bg,
                  priorityConfig(
                    item.item_details?.priority || item.priority || 'none'
                  ).text,
                ]"
              >
                {{
                  t(
                    `PIPELINE.PRIORITY_LABELS.${(item.item_details?.priority || item.priority || 'NONE').toUpperCase()}`
                  )
                }}
              </span>
            </div>
          </div>
        </label>
      </div>

      <!-- Footer -->
      <div class="flex flex-wrap justify-end gap-2">
        <Button
          variant="ghost"
          color="slate"
          size="md"
          :disabled="isSubmitting"
          @click="$emit('close')"
        >
          {{ t('PIPELINE.BULK_ACTIONS.CANCEL') }}
        </Button>
        <Button
          variant="solid"
          color="ruby"
          size="md"
          :disabled="!selectedItems.length || isSubmitting"
          :is-loading="isSubmitting"
          @click="handleConfirm"
        >
          {{
            t('PIPELINE.BULK_ACTIONS.CONFIRM', {
              count: selectedItems.length,
            })
          }}
        </Button>
      </div>
    </div>
  </div>
</template>
